// External control surface: lets MCP servers, parent windows, or page
// scripts drive the editor without needing an LLM API key.
//
// Three channels, all using the same set of tools as the AI assistant:
//   1. window.promoStudio.*    — global API for in-page scripts / DevTools
//   2. URL params              — ?prompt=...  ?project=<base64 json>
//   3. postMessage protocol    — cross-window/iframe control
//
// Same-origin or trusted-iframe usage is assumed; we don't auth callers.

import { executeTool, tools, type ToolDef } from '../ai/tools';
import { reviewAndLoad } from '../security/ImportReviewModal';
import { getState, setProject } from '../store/project';
import type { Project } from '../types';

export interface ExternalAPI {
  /** Get the current project state. */
  getProject(): Project;
  /** Replace the entire project. */
  setProject(project: Project): void;
  /** List the tool definitions (same as MCP / OpenAI / Anthropic). */
  listTools(): ToolDef[];
  /** Call a tool by name. Throws on error. */
  callTool(name: string, args: unknown): unknown;
  /** Encode the current project as a base64 query parameter. */
  serializeProjectParam(): string;
  /** Decode a base64-encoded project param into a Project. */
  deserializeProjectParam(encoded: string): Project;
  /** Programmatically run an AI prompt — fires an event the assistant panel handles. */
  runPrompt(prompt: string): void;
}

const POSTMESSAGE_KIND = 'promo-studio';
const POSTMESSAGE_REPLY = 'promo-studio:reply';
export const PROMPT_EVENT = 'promo-studio:prompt';
export const PROJECT_LOADED_EVENT = 'promo-studio:project-loaded';

export function installExternalAPI(): ExternalAPI {
  const api: ExternalAPI = {
    getProject: () => getState().project,
    setProject: (project) => {
      validateProject(project);
      setProject(project);
    },
    listTools: () => tools,
    callTool: (name, args) => {
      const r = executeTool(name, args);
      if (!r.ok) throw new Error(r.error);
      return r.result;
    },
    serializeProjectParam: () => encodeProject(getState().project),
    deserializeProjectParam: (encoded) => decodeProject(encoded),
    runPrompt: (prompt) => {
      window.dispatchEvent(new CustomEvent(PROMPT_EVENT, { detail: prompt }));
    },
  };

  (window as unknown as { promoStudio: ExternalAPI }).promoStudio = api;

  installPostMessage(api);
  applyUrlParams(api);

  return api;
}

function installPostMessage(api: ExternalAPI): void {
  window.addEventListener('message', (event) => {
    const data = event.data;
    if (!data || typeof data !== 'object' || data.kind !== POSTMESSAGE_KIND) return;

    const reply = (payload: Record<string, unknown>): void => {
      const target = event.source as Window | null;
      if (!target) return;
      target.postMessage(
        { kind: POSTMESSAGE_REPLY, id: data.id ?? null, ...payload },
        event.origin || '*',
      );
    };

    try {
      let result: unknown;
      switch (data.action) {
        case 'callTool':
          result = api.callTool(String(data.name), data.args);
          break;
        case 'getProject':
          result = api.getProject();
          break;
        case 'setProject':
          api.setProject(data.project as Project);
          result = 'ok';
          break;
        case 'listTools':
          result = api.listTools();
          break;
        case 'runPrompt':
          api.runPrompt(String(data.prompt));
          result = 'ok';
          break;
        default:
          throw new Error(`unknown action: ${data.action}`);
      }
      reply({ ok: true, result });
    } catch (err) {
      reply({ ok: false, error: err instanceof Error ? err.message : String(err) });
    }
  });
}

function applyUrlParams(api: ExternalAPI): void {
  const url = new URL(window.location.href);

  const projectParam = url.searchParams.get('project');
  if (projectParam) {
    try {
      const project = decodeProject(projectParam);
      // URL-loaded projects always go through the review modal — they're the
      // most likely vector for a malicious payload (someone pastes a link).
      // Viewer mode skips the review for embed/autoplay use cases.
      const isViewer = url.searchParams.get('viewer') === '1';
      if (isViewer) {
        api.setProject(project);
      } else {
        void reviewAndLoad(project);
      }
      window.dispatchEvent(new CustomEvent(PROJECT_LOADED_EVENT, { detail: { source: 'url' } }));
    } catch (err) {
      console.warn('Failed to load project from URL:', err);
    }
  }

  const promptParam = url.searchParams.get('prompt');
  if (promptParam) {
    setTimeout(() => api.runPrompt(promptParam), 60);
  }

  // Clean params from the URL after consuming so refresh doesn't reapply.
  if (projectParam || promptParam) {
    url.searchParams.delete('project');
    url.searchParams.delete('prompt');
    history.replaceState({}, '', url.toString());
  }
}

function encodeProject(project: Project): string {
  const json = JSON.stringify(project);
  // base64 encode UTF-8 bytes
  const bytes = new TextEncoder().encode(json);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i] ?? 0);
  return btoa(binary);
}

function decodeProject(encoded: string): Project {
  const binary = atob(encoded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  const json = new TextDecoder().decode(bytes);
  const obj = JSON.parse(json) as Project;
  validateProject(obj);
  return obj;
}

function validateProject(obj: unknown): asserts obj is Project {
  const p = obj as Project | null;
  if (!p || p.version !== 1 || !Array.isArray(p.layers) || !p.canvas) {
    throw new Error('Invalid project payload');
  }
}
